import React, { useEffect } from 'react';
import { Box, List, Button, Typography, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';

import Scrollbar from '../../../components/scrollbar';
import { useMutationReadAllNotifications } from '../../../hooks/react-query/useData';

const NotificationsDropdownContent = ({
  NotificationItem,
  token,
  alwaysViewMode,
  allNotificationsData,
  handleClose
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate: readAllNotifications } = useMutationReadAllNotifications(toast, token, queryClient);

  useEffect(() => {
    if (!alwaysViewMode) {
      readAllNotifications();
    }
  }, []);

  const handleViewAll = () => {
    handleClose?.();
    navigate('/dashboard/notifications');
  };

  // Handle both array format and paginated object format
  const notifications = Array.isArray(allNotificationsData)
    ? allNotificationsData
    : allNotificationsData?.notifications || [];

  const unreadCount = notifications?.filter(n => !n.isRead)?.length || 0;

  return (
    <Box
      sx={{
        width: 400,
        maxWidth: '90vw',
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, rgba(142, 113, 173, 0.05) 0%, rgba(168, 75, 156, 0.05) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <NotificationsIcon sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '16px',
                lineHeight: 1.2,
              }}
            >
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  fontSize: '12px',
                }}
              >
                {unreadCount} new {unreadCount === 1 ? 'notification' : 'notifications'}
              </Typography>
            )}
          </Box>
        </Box>
        <CloseIcon
          onClick={handleClose}
          sx={{
            cursor: 'pointer',
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
            },
            transition: 'color 0.2s',
          }}
        />
      </Box>

      {/* Notifications List */}
      {notifications && notifications.length > 0 ? (
        <Scrollbar sx={{ maxHeight: 400, flex: 1 }}>
          <List disablePadding>
            {notifications.slice(0, 20).map((notification, index) => (
              <NotificationItem
                key={notification._id || index}
                notification={notification}
                handleClose={handleClose}
              />
            ))}
          </List>
        </Scrollbar>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            px: 3,
          }}
        >
          <NotificationsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
            No notifications yet
          </Typography>
        </Box>
      )}

      {/* Footer */}
      {notifications && notifications.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              p: 1.5,
              background: 'linear-gradient(135deg, rgba(142, 113, 173, 0.05) 0%, rgba(168, 75, 156, 0.05) 100%)',
            }}
          >
            <Button
              fullWidth
              variant="contained"
              onClick={handleViewAll}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                fontWeight: 600,
                fontSize: '13px',
                py: 1,
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                textTransform: 'none',
              }}
            >
              View All Notifications
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default NotificationsDropdownContent;
